/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { SnackBarService } from "app/shared/services/snack-bar.service";
import { environment } from "environments/environment";

/**
 * Custom Kaleida service for wrapping MSAL (Microsoft Authentication Library) and providing access to the users current access token for connecting to MS Graph, etc.
 */
@Injectable({providedIn: 'root'}) // had problems getting this to inject properly, creating manually from component for now
export class MsalWrapperService {
    static readonly tenantId = environment.microsoftGraphTenantId;
    static readonly redirectUris = location.origin;
    static readonly scopes = ["User.Read", "User.ReadWrite", "Mail.ReadWrite", "Mail.Send"];

    private msalApplication: IPublicClientApplication;
    private initialized: boolean = false;

    constructor(
        private snackbar: SnackBarService,
        private msalService: MsalService,
    ){
        // can't find a way to access the one set up on the module, so instantiate it here
        //this.msalApplication = this.setupMsal();

        // Now done in intitaliseMsal() call as an await is needed.
    }

    
    async setupMsal(): Promise<IPublicClientApplication>{
        // Not sure tenant ID will work, might need tenant "name"
        //  See: https://stackoverflow.com/questions/53526121/use-a-tenant-specific-endpoint-or-configure-the-application-to-be-multi-tenant
        //  It suggests URL is "https://login.microsoftonline.com/MY_TENANT_NAME"
        const authority = "https://login.microsoftonline.com/" + MsalWrapperService.tenantId;  

        return await PublicClientApplication.createPublicClientApplication({
            auth: {
            clientId: environment.microsoftGraphApplicationId,
            //clientId: "a120d2d1-2625-42b1-9baa-e4fa2e2311e1", // SB asked me to try with TEST details
            //postLogoutRedirectUri : MsalWrapperService.redirectUris,                    // doesn't work, just redirects within the popup window and seems to lock up anyway
            authority: authority,                                                         // defaults to "https://login.microsoftonline.com/common"
            redirectUri: MsalWrapperService.redirectUris
            },
            cache: {
            cacheLocation: "localStorage",  // also see SSO details here: https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-sso#with-user-hint
            storeAuthStateInCookie: false,  // May need updating if we are to support IE. However it's assumed that we won't be supporting IE.
            },
        });
    }
    
    async initializeMsal(): Promise<void>{
        if(this.initialized) return;
    
        console.debug("initializeMsal() is initializing MSAL...");
        this.msalApplication = await this.setupMsal();
        await this.msalApplication.initialize();
        await this.msalService.instance.initialize();
        this.initialized=true;
        console.debug("...InitializeMsal() has initialized MSAL.");
    }

    public isInitialized(): boolean{
        return this.initialized;
    }

    async login(username: string): Promise<AuthenticationResult>{
        // Can we do a manual logon without requiring the MsalGuard (which locks out the page completely without an MS logon)?

        // "Alternatively, you can explicitly acquire tokens by using the acquire-token methods as described in the core MSAL.js library."    
        // https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-sign-in?tabs=javascript2

        // See also this method for acquiring token silently, had problems getting it to work though: https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-acquire-token?tabs=javascript2
        // What's the difference betewen acquireTokenPopup() which the other one used and loginPopup() anyway?
        //  acquireTokenPopup() requires scopes whereas loginPopup() has default ones, source: https://stackoverflow.com/questions/65958941/msal-js-loginpopup-vs-acquiretokenpop

        await this.initializeMsal();

        // see https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-sso
        const loginRequest = {
            scopes: MsalWrapperService.scopes,
            loginHint: username
            };
            
        const that = this;


        // Below is based on sign in with popup tutorial JavaScript MSAL.js v2: https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-sign-in?tabs=javascript2
        // Alternatively consider https://learn.microsoft.com/en-us/azure/active-directory/develop/tutorial-v2-angular-auth-code which uses subscriptions etc
        return this.msalApplication
        .loginPopup(loginRequest)
        //.ssoSilent(loginRequest) // throws InteractionRequiredAuthError
        .then(loginResponse=>{
            // Ticketing-Admin security group: c04d9cb9-7d0c-4b18-8943-a16b7be86b46
            return this.checkRoles(loginResponse, environment.microsoftAuthenticationRequiredRole);
        })
        .then(function (loginResponse) {
            // Display signed-in user content, call API, etc.
            if(loginResponse?.account?.homeAccountId!=null)
            {
                console.debug("Calling that.msalService.instance.setActiveAccount(" + loginResponse.account.homeAccountId + ")");
                that.msalService.instance.setActiveAccount(loginResponse.account);
                console.debug("Logged-in to MSAL and acquired access token: " + loginResponse.accessToken + ', set active account to: ' + loginResponse.account.homeAccountId);
            }
            return loginResponse;
        })
        .catch(function (error) {
            //login failure
            console.log("MS login failed: " + error);
            throw error;
        });
    }

    async checkRoles(loginResult: AuthenticationResult, requiredGroupObjectId: string): Promise<AuthenticationResult>{
        // https://grok.com/share/c2hhcmQtMi1jb3B5_c4063ce6-60aa-4c93-a932-d8e91953ce2d
        var memberOfResult = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
            headers: { Authorization: `Bearer ${loginResult.accessToken}`}
        });
        var json = await memberOfResult.json();
        var isInGroup = json.value.some((g: any)=>g.id===requiredGroupObjectId);
        if(!isInGroup){
            // Both these options appear to ask user what account they want to sign out of. Not sure if we can log out silently.
            // If this was part of the main login, we could log them out of G&A though.
            //this.msalService.logoutRedirect();
            //this.msalService.logout();
            throw "User is not a member of the required group.";
        }
        return loginResult;
    }
    

    async logout(){
        // See: https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-sign-in?tabs=angular2
        // If we're in a protected route, it looks like we need to redirect to a non-protected route (one that doesn't need MSAL).
        // Otherwise we can probably log out explicitly?

        // Noticed a little odd behavior when signed in to multiple accounts at once. It appears to sign out and then prompt the user
        // for which account to sign out of even though it's already done it?
        // Also, closing the prompt appears to lock up the application. This seems to happen regardless of whether we await the logoutPopup() call or not.

        var that=this;
        await that.initializeMsal();

        console.debug("logout() called, getting active account...");
        var activeAccount = that.msalService.instance.getActiveAccount();
        console.debug(" active account: " + activeAccount?.homeAccountId);

        const logoutRequest = {
            //account: that.msalApplication.getAccountByHomeId(that.account.homeAccountId),
            //account: that.account,
            //mainWindowRedirectUri: MsalWrapperService.redirectUris

            // Attempt to log out without pop-up as requested by SB, see https://stackoverflow.com/questions/70456800/msal-logout-displaying-multiple-account
            // May require login_hint claim to be set up under Token Configuration in Azure.
            account: activeAccount,
        };
            
        //await that.msalApplication.logoutPopup(logoutRequest);
        console.debug(" calling logoutPopup(...)...");
        that.msalApplication.logoutPopup(logoutRequest);    // test to see if skipping the await resolves hanging issue post-logout. It does, though does make the caller think user has logged out before they fully have been.
        console.debug("logout() completed.");
    }

    async isLoggedIn(): Promise<boolean>{
        await this.initializeMsal();
        return this.msalService.instance.getAllAccounts().length>0;
    }

    async getLoggedInName(): Promise<string>{
        const account = await this.getMainAccount();
        return account?.username;
    }

    async getLoggedInNames(): Promise<string>{
        const accounts = await this.getAccounts();

        if(accounts==null || accounts.length==0) return null;
        var names = accounts.map(a=>a.username);
        return names.join(", ");
    }

    /** Gets all available accounts. */
    async getAccounts(): Promise<AccountInfo[]>{
        const isLoggedIn = await this.isLoggedIn();
        return isLoggedIn?this.msalService.instance.getAllAccounts():[];
    }

    /** Tries to get the most appropriate account to use. */
    async getMainAccount(): Promise<AccountInfo>{
        const allAccounts = await this.getAccounts();
        if(allAccounts.length==0) return null;
        if(allAccounts.length==1) return allAccounts[0];

        // Based on ideas found in  https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa/blob/main/App/authPopup.js
        console.warn("MsalWrapperService found multiple signed-in MS accounts.");
        console.debug(this.getAccountsDump());
        console.warn("No preference rules have been defined so will use first account in list. Access token and other requests will be tied to that account.");
        return allAccounts[0];
    }

    async getAccountByUsername(username: string): Promise<AccountInfo>{
        /*
        const usernameLower = username.toLowerCase();
        const allAccounts = await this.getAccounts();
        var match=allAccounts.find(a=>a.username.toLowerCase()==usernameLower);
        return match ?? this.getMainAccount();
        */
       return this.msalService.instance.getAccountByUsername(username);
    }

    async getAccountsDump(): Promise<string>{
        var dump: string = '';
        var allAccounts = await this.getAccounts();
        allAccounts.forEach(acc=>{
            dump+="\t" + JSON.stringify(acc) + "\r\n";
        });
        return dump;
    }

    /**
     * @see: {@link "https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-acquire-token?tabs=javascript2#acquire-a-token-with-a-pop-up-window"}
     * Modified MS example to return promise for the access token rather than calling an API directly.
     */
    async getAccessToken(username: string): Promise<string>{
        const account = await (username==null?this.getMainAccount(): this.getAccountByUsername(username));
        
        const accessTokenRequest = {
            scopes: MsalWrapperService.scopes,
            account:  account,
          };

        var that = this;
        var promise = that.msalApplication
        .acquireTokenSilent(accessTokenRequest)
        .then(function (accessTokenResponse) {
          // Acquire token silent success
          console.debug("acquireTokenSilent(...) success.");
          // Return acquired token
          return accessTokenResponse.accessToken;
        })
        .catch(function (error) {
          //Acquire token silent failure, and send an interactive request
          console.warn("acquireTokenSilent(...) failed.");

          // KD0002034: Seem to be getting BrowserAuthErrors back when user not signed in rather than InteractionRequiredAuthError.
          // For now, will ignore type check and always prompt for sign-in

          //if (error instanceof InteractionRequiredAuthError) {
            //console.debug("Error was InteractionRequiredAuthError so attempting acquireTokenPopup(...)...")
            return that.msalApplication
              .acquireTokenPopup(accessTokenRequest)
              .then(function (accessTokenResponse) {
                // Acquire token interactive success
                console.debug("acquireTokenPopup(...) success.");
                // Return acquired token
                return accessTokenResponse.accessToken;
              })
              .catch(function (error) {
                // Acquire token interactive failure
                console.warn("acquireTokenPopup(...) failed.");
                console.log(error);
                throw error;
              });
          /*}
          else{
            console.log(error);
            throw error;
          }*/
        });

        return promise;
    }
}